import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  Box,
} from '@material-ui/core';
import { getAvailableOrders, claimOrder } from '../../api/shopperOrders';
import {
  setAvailableOrders,
  setLoading,
  setError,
} from '../../features/shopperOrders/shopperOrdersSlice';
import { getToken } from '../../services/auth';

export const AvailableOrders = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { availableOrders, loading } = useSelector(
    (state) => state.shopperOrders,
  );

  const fetchOrders = async () => {
    dispatch(setLoading(true));
    try {
      const response = await getAvailableOrders({ token: getToken() });
      dispatch(setAvailableOrders(response.results || []));
    } catch (error) {
      dispatch(setError(error.message));
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleClaim = (order) => {
    const orderType =
      order.orderType || order.type || order.delivery_type || '';
    if (order.shapyer && order.shapyer !== '-') {
      navigate(`/shopper/order/${order.id}`);
    } else {
      navigate(
        `/shopper/assign/${order.reference}/${order.id}${
          orderType ? `?orderType=${orderType}` : ''
        }`,
      );
    }
  };
  const handleReferenceClick = (order) => {
    const orderType =
      order.orderType || order.type || order.delivery_type || '';
    if (order.shapyer && order.shapyer !== '-') {
      navigate(`/shopper/order/${order.id}`);
    } else {
      navigate(
        `/shopper/assign/${order.reference}/${order.id}${
          orderType ? `?orderType=${orderType}` : ''
        }`,
      );
    }
  };

  const orders = (availableOrders || []).filter(function (order) {
    var status =
      order.status && typeof order.status === 'object'
        ? order.status.description || ''
        : order.status || '';
    return status !== 'CANCELED' && status !== 'DELIVERED';
  });

  return (
    <Box mb={4}>
      <Box mb={3}>
        <Typography variant="h4">
          {t('ShopperOrders.availableTitle', 'Available Orders')}
        </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 'bold' }}>
                {t('ShopperOrders.reference', 'Reference')}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {t('ShopperOrders.orderType', 'Order Type')}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {t('ShopperOrders.client', 'Client')}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {t('ShopperOrders.shopper', 'Shopper')}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {t('ShopperOrders.status', 'Status')}
              </TableCell>
              <TableCell align="right" style={{ fontWeight: 'bold' }}>
                {t('ShopperOrders.actions', 'Actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((order) => {
              const statusText =
                order.status && typeof order.status === 'object'
                  ? order.status.description || 'N/A'
                  : order.status || 'N/A';

              let clientName = 'Unknown';
              if (order.client && order.client.user) {
                const u = order.client.user;
                clientName =
                  `${u.firstName || u.first_name || ''} ${
                    u.lastName || u.last_name || ''
                  }`.trim() ||
                  u.username ||
                  'Unknown';
              } else if (typeof order.client_name === 'string') {
                clientName = order.client_name;
              } else if (typeof order.client === 'string') {
                clientName = order.client;
              } else if (order.client && typeof order.client === 'object') {
                clientName =
                  order.client.first_name ||
                  order.client.firstName ||
                  order.client.name ||
                  'Unknown';
              } else if (order.user && typeof order.user === 'object') {
                clientName =
                  `${order.user.first_name || order.user.firstName || ''} ${
                    order.user.last_name || order.user.lastName || ''
                  }`.trim() ||
                  order.user.username ||
                  'Unknown';
              }

              return (
                <TableRow key={order.reference}>
                  <TableCell component="th" scope="row">
                    <span
                      style={{
                        cursor: 'pointer',
                        color: '#3f51b5',
                        textDecoration: 'underline',
                      }}
                      onClick={() => handleReferenceClick(order)}>
                      {order.reference}
                    </span>
                  </TableCell>
                  <TableCell>
                    {order.orderType ||
                      order.type ||
                      order.delivery_type ||
                      'N/A'}
                  </TableCell>
                  <TableCell>{clientName}</TableCell>
                  <TableCell>
                    {order.shapyer && order.shapyer !== '-'
                      ? order.shapyer
                      : 'Unasigned'}
                  </TableCell>
                  <TableCell>{statusText}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleClaim(order)}>
                      {order.shapyer && order.shapyer !== '-'
                        ? t('ShopperOrders.view', 'View')
                        : t('ShopperOrders.claim', 'Assign')}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {orders.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('ShopperOrders.noOrders', 'No available orders')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
